import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import { Column, Conditional, Else, Heading, If, makeObjectFromArrayMap, Row, TruncatedText } from "@hex-insights/core";
import {
	Form,
	FormSubmitFunction,
	FormType,
	submissionFailure,
	submissionSuccess,
	SubmitButton,
	useFormState,
	useInternalField,
	useOnSuccess,
} from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import {
	ButtonLink,
	ClassYearUtilities,
	Contract,
	ContractUtilities,
	Icon,
	ModelFormat,
	OrderDirection,
	ReEnrollmentMealPlanContractListQuery,
	Service,
	ServiceCategory,
	ServiceFormat,
	ServiceOrderField,
	Student,
	StudentComponents,
	StudentFormat,
	StudentNameAndImageFragment,
	StudentUtilities,
	useReEnrollmentMealPlanContractListQuery,
	useRemoveMealPlanFromContractMutation,
	useServiceInformationListQuery,
	useSetMealPlanOnContractMutation,
} from "@hex-insights/verita-romania.shared";
import { ContractLockedNotice, ReEnrollmentLayout } from "../Components";
import { reEnrollmentDataConsentPageInfo } from "../DataConsentPage/pageinfo";
import { reEnrollmentPaymentPlansPageInfo } from "../PaymentPlansPage/pageinfo";
import { getReEnrollmentContractFilter } from "../Utilities";
import { reEnrollmentMealPlansPageInfo } from "./pageinfo";
import generalStyles from "../general.module.css";
import styles from "./styles.module.css";

export function ReEnrollmentMealPlansPage() {
	useActivePageRegistration(reEnrollmentMealPlansPageInfo);

	const { data } = useReEnrollmentMealPlanContractListQuery({
		variables: {
			filters: [getReEnrollmentContractFilter()],
		},
		fetchPolicy: "network-only",
	});

	if (!data) {
		return (
			<ReEnrollmentLayout>
				<div className={styles["main-content"]}>
					<TopContent />

					<div>Loading...</div>
				</div>
			</ReEnrollmentLayout>
		);
	}

	return <LoadedContent data={data} />;
}

type FormValues = {
	mealPlans: MealPlanFormValues[];
};

type MealPlanFormValues = {
	contractID: Contract["id"];
	mealPlanID: Service["id"] | null;
};

type LoadedContentProps = {
	data: ReEnrollmentMealPlanContractListQuery;
};

function LoadedContent({ data }: LoadedContentProps) {
	const { contractStudentMap, contractStatusMap } = React.useMemo(() => {
		return {
			contractStudentMap: makeObjectFromArrayMap(data.contractConnection.edges, (e) => [e.node.id, e.node.student]),
			contractStatusMap: makeObjectFromArrayMap(data.contractConnection.edges, (e) => [e.node.id, e.node.status]),
		};
	}, [data]);

	const formState = useFormState<FormValues>({
		initialFormValues: () => ({
			mealPlans: data.contractConnection.edges.map((e) => ({
				contractID: e.node.id,
				mealPlanID: e.node.contractLineItems[0]?.serviceID ?? null,
			})),
		}),
		formType: FormType.Create,
	});
	useInternalField(formState, "mealPlans");

	const {
		formSetFunctions: { mealPlans: setMealPlans },
	} = formState;
	const setMealPlanID = React.useCallback(
		(contractID: Contract["id"], mealPlanID: Service["id"] | null) => {
			setMealPlans((prev) => prev.map((e) => (e.contractID === contractID ? { ...e, mealPlanID } : e)));
		},
		[setMealPlans],
	);

	const [setMealPlanOnContract] = useSetMealPlanOnContractMutation();
	const [removeMealPlanFromContract] = useRemoveMealPlanFromContractMutation();
	const onSubmit = React.useCallback<FormSubmitFunction<FormValues>>(
		async ({ formValues }) => {
			const promises = [];
			for (let i = 0; i < formValues.mealPlans.length; i++) {
				const { contractID, mealPlanID } = formValues.mealPlans[i];
				if (!mealPlanID) {
					promises.push(removeMealPlanFromContract({ variables: { contractID } }));
				} else {
					promises.push(setMealPlanOnContract({ variables: { contractID, mealPlanID } }));
				}
			}

			const results = await Promise.all(promises);
			for (let i = 0; i < results.length; i++) {
				const result = results[i];
				if (result.errors) {
					return submissionFailure([{ _: ["An error occurred while saving your choices. Please try again."] }]);
				}
			}

			return submissionSuccess();
		},
		[setMealPlanOnContract, removeMealPlanFromContract],
	);

	const history = useHistory();
	useOnSuccess(formState, () => history.push(reEnrollmentPaymentPlansPageInfo.to));

	return (
		<ReEnrollmentLayout
			footerContent={
				<Row justify="space-between" align="center">
					<ButtonLink variant="link" to={reEnrollmentDataConsentPageInfo.to}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.ArrowLeft size={24} /> Back
						</Row>
					</ButtonLink>

					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Continue
					</SubmitButton>
				</Row>
			}
		>
			<div className={styles["main-content"]}>
				<TopContent />

				<Form
					formState={formState}
					onSubmit={formState.onSubmitWrapper(onSubmit)}
					name="reEnrollment.mealPlans"
					noNotifications
					noNavigationPrompt
					mergeUnsavedChangesAutomatically
				>
					<Column justify="spaced-start" verticalSpacing="2.5rem">
						{formState.formValues.mealPlans.map((e) => (
							<ContractMealPlanField
								key={e.contractID}
								student={contractStudentMap[e.contractID]}
								contractID={e.contractID}
								contractStatus={contractStatusMap[e.contractID]}
								mealPlanID={e.mealPlanID}
								setMealPlanID={setMealPlanID}
							/>
						))}

						<span className={styles["notice"]}>
							<span className={styles["notice__asterisk"]}>*</span>Note: For the 2024-2025 academic year, if inflation
							or cost of food goes up more than 10%, Verita reserves the right to amend the food pricing per semester
							(August, February).
						</span>
					</Column>
				</Form>
			</div>
		</ReEnrollmentLayout>
	);
}

type ContractMealPlanFieldProps = {
	student: Pick<Student, "id" | "graduationYear" | "yearsFromGraduation"> & StudentNameAndImageFragment;
	contractID: Contract["id"];
	contractStatus: Contract["status"];
	mealPlanID: Service["id"] | null;
	setMealPlanID: (contractID: Contract["id"], mealPlanID: Service["id"] | null) => void;
};

function ContractMealPlanField({
	student,
	contractID,
	contractStatus,
	mealPlanID,
	setMealPlanID,
}: ContractMealPlanFieldProps) {
	const { loading: loadingClassYears, classYears } = ClassYearUtilities.useClassYearsMap();
	const nextClassYear = StudentUtilities.getNextClassYear(student, classYears);

	const {
		loading: loadingMealPlans,
		data: mealPlanData,
		error: mealPlanError,
	} = useServiceInformationListQuery({
		variables: {
			filters: {
				isActiveEQ: true,
				categoryEQ: ServiceCategory.MealPlan,
				or: [{ hasClassYears: false }, { classYears: [{ idEQ: nextClassYear?.id }] }],
			},
			order: { field: ServiceOrderField.Price, direction: OrderDirection.Asc },
		},
		skip: loadingClassYears,
	});
	const queryStatus = getListQueryStatus({
		loading: loadingMealPlans,
		data: mealPlanData,
		error: mealPlanError,
		resultKey: "serviceConnection",
	});

	const isLoading = loadingClassYears || loadingMealPlans;

	const changesAllowed = ContractUtilities.allowsParentChanges(contractStatus);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (!changesAllowed) {
				return;
			}
			if (event.target.checked) {
				setMealPlanID(contractID, event.target.value === "null" ? null : event.target.value);
			}
		},
		[setMealPlanID, contractID, changesAllowed],
	);

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem">
			<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ flexGrow: 1, minWidth: 0 }}>
				<StudentComponents.StudentIcon student={student} size={64} />

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
					<TruncatedText className={styles["meal-plan-field__student-name"]} style={{ minWidth: 0 }}>
						{StudentFormat.name(student)}
					</TruncatedText>
				</Column>
			</Row>

			<If condition={!changesAllowed}>
				<ContractLockedNotice />
			</If>

			<Conditional>
				<If condition={isLoading && !!mealPlanData}>
					<div>Loading...</div>
				</If>
				<Else>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<label className={styles["meal-plan-field__option"]}>
							<input
								type="radio"
								name={contractID}
								value="null"
								checked={mealPlanID === null}
								onChange={onChange}
								disabled={!changesAllowed}
							/>

							<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ flexGrow: 1 }}>
								<Row justify="space-between" style={{ gap: "1rem" }}>
									<span className={styles["meal-plan-field__option__name"]}>No Meal Plan</span>
									<span className={styles["meal-plan-field__option__price"]}>{ModelFormat.money(0)}/Year</span>
								</Row>
								<span className={styles["meal-plan-field__option__description"]}>
									Student will bring their own meals and snacks.
								</span>
							</Column>
						</label>
						<Conditional>
							<If condition={isLoading || queryStatus === "loading"}>
								<span>Loading...</span>
							</If>
							<If condition={queryStatus === "results"}>
								{mealPlanData?.serviceConnection.edges.map(({ node: mealPlan }) => (
									<label key={mealPlan.id} className={styles["meal-plan-field__option"]}>
										<input
											type="radio"
											name={contractID}
											value={mealPlan.id}
											checked={mealPlanID === mealPlan.id}
											onChange={onChange}
											disabled={!changesAllowed}
										/>

										<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ flexGrow: 1 }}>
											<Row justify="space-between" style={{ gap: "1rem" }}>
												<span className={styles["meal-plan-field__option__name"]}>{ServiceFormat.name(mealPlan)}</span>
												<span className={styles["meal-plan-field__option__price"]}>
													{ModelFormat.money(mealPlan.price)}/Year
												</span>
											</Row>
											<If condition={!!mealPlan.description}>
												<span className={styles["meal-plan-field__option__description"]}>{mealPlan.description}</span>
											</If>
										</Column>
									</label>
								))}
							</If>
							<If condition={queryStatus === "no-results"}>
								<span>No meal plan options available</span>
							</If>
							<If condition={queryStatus === "error"}>
								<span>Something went wrong. Please refresh the page.</span>
							</If>
						</Conditional>
					</Column>
				</Else>
			</Conditional>
		</Column>
	);
}

function TopContent() {
	return (
		<React.Fragment>
			<Heading.H1 className={generalStyles["heading"]}>Please select meal plans</Heading.H1>
		</React.Fragment>
	);
}
